import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  ItBacklogItem, 
  CreateBacklogItemData, 
  UpdateBacklogItemData,
  PRIORITY_CONFIG,
  CATEGORY_CONFIG,
  SYSTEM_MODULES,
  BacklogPriority,
  BacklogCategory,
} from '@/types/itBacklog';
import { useCreateBacklogItem, useUpdateBacklogItem, useBugReports, useAdminUsers } from '@/hooks/useItBacklog';
import { Loader2 } from 'lucide-react';

const formSchema = z.object({
  title: z.string().min(3, 'Título deve ter pelo menos 3 caracteres'),
  description: z.string().optional(),
  acceptance_criteria: z.string().optional(),
  priority: z.enum(['low', 'medium', 'high', 'urgent', 'critical']),
  category: z.enum(['bug_fix', 'feature', 'improvement', 'infrastructure', 'support', 'task']),
  module: z.string().optional(),
  estimated_hours: z.coerce.number().min(0).optional(),
  due_date: z.string().optional(),
  related_bug_report_id: z.string().optional(),
  assigned_to: z.string().optional(),
  notes: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

interface BacklogItemFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item?: ItBacklogItem | null;
}

export function BacklogItemForm({ open, onOpenChange, item }: BacklogItemFormProps) {
  const isEditing = !!item;
  const createItem = useCreateBacklogItem();
  const updateItem = useUpdateBacklogItem();
  const { data: bugReports } = useBugReports();
  const { data: adminUsers } = useAdminUsers();
  
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: item?.title || '',
      description: item?.description || '',
      acceptance_criteria: item?.acceptance_criteria || '',
      priority: item?.priority || 'medium',
      category: item?.category || 'task',
      module: item?.module || '',
      estimated_hours: item?.estimated_hours || undefined,
      due_date: item?.due_date || '',
      related_bug_report_id: item?.related_bug_report_id || '',
      assigned_to: item?.assigned_to || '',
      notes: item?.notes || '',
    },
  });
  
  // Reset form when item changes (for editing)
  useEffect(() => {
    if (open) {
      form.reset({
        title: item?.title || '',
        description: item?.description || '',
        acceptance_criteria: item?.acceptance_criteria || '',
        priority: item?.priority || 'medium',
        category: item?.category || 'task',
        module: item?.module || '',
        estimated_hours: item?.estimated_hours || undefined,
        due_date: item?.due_date || '',
        related_bug_report_id: item?.related_bug_report_id || '',
        assigned_to: item?.assigned_to || '',
        notes: item?.notes || '',
      });
    }
  }, [open, item, form]);
  
  const onSubmit = async (data: FormData) => {
    try {
      const payload: CreateBacklogItemData | UpdateBacklogItemData = {
        title: data.title,
        description: data.description || undefined,
        acceptance_criteria: data.acceptance_criteria || undefined,
        priority: data.priority,
        category: data.category,
        module: data.module || undefined,
        estimated_hours: data.estimated_hours || undefined,
        due_date: data.due_date || undefined,
        related_bug_report_id: data.related_bug_report_id || undefined,
        assigned_to: data.assigned_to || undefined,
        notes: data.notes || undefined,
      };
      
      if (isEditing && item) {
        await updateItem.mutateAsync({ id: item.id, data: payload });
      } else {
        await createItem.mutateAsync(payload as CreateBacklogItemData);
      }
      
      onOpenChange(false);
      form.reset();
    } catch (error) {
      console.error('Error saving item:', error);
    }
  };
  
  const isLoading = createItem.isPending || updateItem.isPending;
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Editar Tarefa' : 'Nova Tarefa'}
          </DialogTitle>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Título *</FormLabel>
                  <FormControl>
                    <Input placeholder="Título da tarefa" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Descrição</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Descreva a tarefa em detalhes..." 
                      className="min-h-[100px]"
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="priority"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Prioridade</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {(Object.keys(PRIORITY_CONFIG) as BacklogPriority[]).map((key) => (
                          <SelectItem key={key} value={key}>
                            {PRIORITY_CONFIG[key].icon} {PRIORITY_CONFIG[key].label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Categoria</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {(Object.keys(CATEGORY_CONFIG) as BacklogCategory[]).map((key) => (
                          <SelectItem key={key} value={key}>
                            {CATEGORY_CONFIG[key].icon} {CATEGORY_CONFIG[key].label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="module"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Módulo</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o módulo" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {SYSTEM_MODULES.map((mod) => (
                          <SelectItem key={mod} value={mod}>
                            {mod}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="estimated_hours"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Horas Estimadas</FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        placeholder="Ex: 4" 
                        min="0" 
                        step="0.5"
                        {...field} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="due_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Data Limite</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="assigned_to"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Responsável</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {adminUsers?.map((user) => (
                          <SelectItem key={user.id} value={user.id}>
                            {user.display_name || `${user.first_name} ${user.last_name}`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <FormField
              control={form.control}
              name="related_bug_report_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Vincular a Feature/Improvement</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione uma solicitação (opcional)" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {bugReports?.map((item: any) => (
                        <SelectItem key={item.id} value={item.id}>
                          {item.type === 'feature' ? '✨' : '📈'} [{item.status}] {item.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="acceptance_criteria"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Critérios de Aceite</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="O que precisa estar pronto para considerar a tarefa concluída?" 
                      className="min-h-[80px]"
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notas</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Notas adicionais..." 
                      className="min-h-[60px]"
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <div className="flex justify-end gap-2 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => onOpenChange(false)}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {isEditing ? 'Salvar' : 'Criar Tarefa'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
